// Settings Store - SAT V4 Zone
import { create } from 'zustand';
import type { AutomationProfile } from '../types';
import { mockAutomationProfiles } from '../lib/mockData';

interface SettingsState {
  // Data
  automationProfiles: AutomationProfile[];
  activeProfileId: string | null;

  // UI State
  isDarkMode: boolean;
  language: string;
  scheduleType: 'Weekly' | 'Bi-Weekly';

  // Actions - Automation Profiles
  addProfile: (profile: AutomationProfile) => void;
  updateProfile: (id: string, updates: Partial<AutomationProfile>) => void;
  deleteProfile: (id: string) => void;
  setActiveProfile: (id: string | null) => void;
  setDefaultProfile: (id: string) => void;

  // Actions - UI Settings
  toggleDarkMode: () => void;
  setLanguage: (lang: string) => void;
  setScheduleType: (type: 'Weekly' | 'Bi-Weekly') => void;

  // Getters
  getActiveProfile: () => AutomationProfile | undefined;
  getDefaultProfile: () => AutomationProfile | undefined;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  // Initialize with mock data
  automationProfiles: mockAutomationProfiles,
  activeProfileId: 'profile-001',

  isDarkMode: false,
  language: 'en',
  scheduleType: 'Bi-Weekly',

  // Profile actions
  addProfile: (profile) =>
    set((state) => ({
      automationProfiles: [...state.automationProfiles, profile],
    })),

  updateProfile: (id, updates) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.map((p) =>
        p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
      ),
    })),

  deleteProfile: (id) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.filter((p) => p.id !== id),
      // If deleting active profile, clear selection
      activeProfileId: state.activeProfileId === id ? null : state.activeProfileId,
    })),

  setActiveProfile: (id) => set({ activeProfileId: id }),

  setDefaultProfile: (id) =>
    set((state) => ({
      automationProfiles: state.automationProfiles.map((p) => ({
        ...p,
        isDefault: p.id === id,
      })),
    })),

  // UI actions
  toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
  setLanguage: (lang) => set({ language: lang }),
  setScheduleType: (type) => set({ scheduleType: type }),

  // Getters
  getActiveProfile: () => {
    const state = get();
    return state.automationProfiles.find((p) => p.id === state.activeProfileId);
  },

  getDefaultProfile: () => get().automationProfiles.find((p) => p.isDefault),
}));

export default useSettingsStore;

